import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Plus, Trash2, FileText, Download, AlertCircle, Scan, Image as ImageIcon, UploadCloud, ClipboardList } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import toast from 'react-hot-toast';
import PizZip from 'pizzip';
import Docxtemplater from 'docxtemplater';
import ImageModule from 'docxtemplater-image-module-free';
import { saveAs } from 'file-saver';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';
import SearchableCombobox from '@/components/ui/SearchableCombobox';
import { createWorker } from 'tesseract.js';
import { cn } from '@/lib/utils';

export default function WorkspaceDIPGenerator() {
  const [brandName, setBrandName] = useState('');
  const [variantName, setVariantName] = useState('');
  const [kemasanList, setKemasanList] = useState([{ name: '' }]);
  const [warnaSediaan, setWarnaSediaan] = useState('Bening kekuningan');
  const [vendors, setVendors] = useState([{ name: '' }]);
  const [ingredients, setIngredients] = useState([{ name: '', function: '', cas: '', percentage: '' }]);
  
  const [isGenerating, setIsGenerating] = useState(false);
  const [isScanning, setIsScanning] = useState(false);
  const [isScanModalOpen, setIsScanModalOpen] = useState(false);
  const [isPasteModalOpen, setIsPasteModalOpen] = useState(false);
  const [pastedText, setPastedText] = useState('');
  const [dragActive, setDragActive] = useState(false);

  const fileInputRef = useRef(null);

  const queryClient = useQueryClient();

  // Fetch Suppliers for dropdown
  const { data: suppliers = [], isLoading: isLoadingSuppliers } = useQuery({
    queryKey: ['supplier-database'],
    queryFn: async () => {
      try {
        const anonKey = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJyb2xlIjoiYW5vbiIsImlzcyI6InN1cGFiYXNlIiwiaWF0IjoxNzgwMjIxMTk3LCJleHAiOjE5Mzc5MDExOTd9.o9VzBr1CisDKFlNoSDQaW2gTbiasqS2_Y-qgqdEbRpg';
        const res = await fetch('https://db.sataladermatechessential.com/rest/v1/SupplierDatabase?select=*', {
          headers: {
            'apikey': anonKey,
            'Authorization': `Bearer ${anonKey}`
          }
        });
        const data = await res.json();
        return (data || [])
          .filter(row => !row.data?.deleted_at)
          .map(row => ({ ...row.data, id: row.id }));
      } catch (err) {
        return [];
      }
    }
  });

  // Fetch CompanySettings for Warna Sediaan options
  const { data: settingsList = [] } = useQuery({
    queryKey: ['company-settings'],
    queryFn: () => base44.entities.CompanySettings.list(),
  });
  const settings = settingsList[0] || {};
  const warnaOptionsDb = settings.warna_sediaan_options || ['Bening kekuningan', 'Bening'];

  const saveWarnaMutation = useMutation({
    mutationFn: async (newOption) => {
      const updatedOptions = Array.from(new Set([...warnaOptionsDb, newOption]));
      if (settings.id) {
        return base44.entities.CompanySettings.update(settings.id, { warna_sediaan_options: updatedOptions });
      } else {
        return base44.entities.CompanySettings.create({ warna_sediaan_options: updatedOptions });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['company-settings']);
    }
  });

  const handleWarnaChange = (val) => {
    setWarnaSediaan(val);
    if (val && !warnaOptionsDb.includes(val)) {
      saveWarnaMutation.mutate(val);
    }
  };

  const supplierOptions = suppliers.map(s => ({ value: s.nama_sales, label: s.nama_sales }));

  // Handlers for dynamic lists
  const addVendor = () => setVendors([...vendors, { name: '' }]);
  const removeVendor = (index) => setVendors(vendors.filter((_, i) => i !== index));
  const updateVendor = (index, value) => {
    const newVendors = [...vendors];
    newVendors[index].name = value;
    setVendors(newVendors);
  };

  const addKemasan = () => setKemasanList([...kemasanList, { name: '' }]);
  const removeKemasan = (index) => setKemasanList(kemasanList.filter((_, i) => i !== index));
  const updateKemasan = (index, value) => {
    const newList = [...kemasanList];
    newList[index].name = value;
    setKemasanList(newList);
  };

  const addIngredient = () => setIngredients([...ingredients, { name: '', function: '', cas: '', percentage: '' }]);
  const removeIngredient = (index) => setIngredients(ingredients.filter((_, i) => i !== index));
  const updateIngredient = (index, field, value) => {
    const newIngredients = [...ingredients];
    newIngredients[index][field] = value;
    setIngredients(newIngredients);
  };

  // OCR Processing Logic
  const parseTextLinesToIngredients = (textLines, isOcr = false) => {
    const parsedIngredients = [];
    const commonFuncs = [
      'solvent', 'sodvent', 'solvcnt', 'solvont', 'solyent', 'soivent', 'solvant',
      'fragrance', 'frapnrca', 
      'absorbent', 'absorbant', 
      'colorant', 'ingredients', 'preservative', 'surfactant',
      'protecting', 'conditioning', 'emollient', 'emulsifying', 'viscosity', 
      'antioxidant', 'buffering', 'humectant', 'cleansing', 'soothing',
      'antimicrobial', 'masking', 'absorber', 'chelating'
    ];
    const funcMap = {
      'sodvent': 'solvent', 'solvcnt': 'solvent', 'solvont': 'solvent', 'solyent': 'solvent', 'soivent': 'solvent', 'solvant': 'solvent',
      'absorbant': 'absorbent', 'frapnrca': 'fragrance'
    };
    
    textLines.forEach(line => {
      let rawLine = line.trim();
      const lower = rawLine.toLowerCase();
      
      if (!rawLine || 
          lower.includes('nama bahan') || lower.includes('nama baham') || 
          lower.includes('hama bahan') || lower.includes('hama baham') || 
          lower.includes('bamam') || lower.includes('baham') || 
          (lower.includes('fungsi') && lower.includes('cas')) ||
          (lower.includes('cas') && lower.includes('percentage')) ||
          (lower.includes('cas') && lower.includes('group')) ||
          lower.includes('percentage (%) group') ||
          lower.includes('keterangan lampiran') || 
          lower.includes('lampiran 1') || lower.includes('lampiran 2') ||
          lower.includes('total percentage') || lower.includes('total fercentage') || 
          lower.includes('data kemasan') || lower.includes('data bahan')) {
        return;
      }

      rawLine = rawLine.replace(/^(.{1,3}[\.,]\s+|[0-9]{1,2}[\.,;]?\s+|q[il]\s+|[sSliIO]\s+|@\.\s+)/i, '');

      let cas = '';
      let percentage = '';
      let func = '';
      
      // Prevent matching peg-40 as CAS
      rawLine = rawLine.replace(/peg-40/ig, 'PEG_40');
      
      const casRegex = isOcr 
        ? /\b([A-Za-z0-9]{2,7}-[A-Za-z0-9]{1,3}(?:-[A-Za-z0-9]{1,2})?)\b/
        : /\b([0-9]{2,7}-[0-9]{1,3}-[0-9]{1,2})\b/;
        
      const casMatch = rawLine.match(casRegex);
      if (casMatch) {
        if (isOcr) {
          cas = casMatch[1].toUpperCase()
            .replace(/[OQ]/g, '0')
            .replace(/[Il|]/g, '1')
            .replace(/[Z]/g, '2')
            .replace(/[S]/g, '5')
            .replace(/[A]/g, '4')
            .replace(/[G]/g, '6')
            .replace(/[T]/g, '7')
            .replace(/[BRPE]/g, '8')
            .replace(/\s+/g, '');
          
          if ((cas.match(/-/g) || []).length === 1) {
             cas = cas.slice(0, cas.length - 1) + '-' + cas.slice(cas.length - 1);
          }
        } else {
          cas = casMatch[1];
        }
        rawLine = rawLine.replace(casMatch[0], ' ').trim();
      }
      
      rawLine = rawLine.replace(/PEG_40/g, 'peg-40');
      
      rawLine = rawLine.replace(/\bcas\s*no\.?:?\b/ig, ' ').trim();

      let tokens = rawLine.split(/\s+/);
      for (let i = tokens.length - 1; i >= 0; i--) {
        const tLower = tokens[i].toLowerCase();
        if (commonFuncs.includes(tLower)) {
          const mappedFunc = funcMap[tLower] || tLower;
          if (mappedFunc === 'ingredients' && i > 0 && (tokens[i-1].toLowerCase() === 'fragrance' || funcMap[tokens[i-1].toLowerCase()] === 'fragrance')) {
            func = 'fragrance ingredients';
            tokens.splice(i-1, 2);
            break;
          } else if (mappedFunc === 'colorant' && i > 0 && tokens[i-1].toLowerCase() === 'cosmetic') {
            func = 'cosmetic colorant';
            tokens.splice(i-1, 2);
            break;
          } else if ((mappedFunc === 'protecting' || mappedFunc === 'conditioning') && i > 0 && (tokens[i-1].toLowerCase() === 'skin' || tokens[i-1].toLowerCase() === 'hair')) {
            func = `${tokens[i-1].toLowerCase()} ${mappedFunc}`;
            tokens.splice(i-1, 2);
            break;
          } else if (mappedFunc === 'viscosity' && i < tokens.length - 1 && tokens[i+1].toLowerCase() === 'controlling') {
            func = 'viscosity controlling';
            tokens.splice(i, 2);
            break;
          } else if (mappedFunc === 'absorber' && i > 0 && tokens[i-1].toLowerCase() === 'uv') {
            func = 'uv absorber';
            tokens.splice(i-1, 2);
            break;
          } else {
            func = mappedFunc;
            tokens.splice(i, 1);
            break;
          }
        }
      }

      for (let i = tokens.length - 1; i >= 0; i--) {
        const t = tokens[i];
        if (/\d/.test(t) && !/[a-zA-Z]{4,}/.test(t)) {
           let val = t.toUpperCase()
             .replace(/[OQ]/g, '0')
             .replace(/[Il|]/g, '1')
             .replace(/[Z]/g, '2')
             .replace(/[S]/g, '5')
             .replace(/[A]/g, '4')
             .replace(/[G]/g, '6')
             .replace(/[T]/g, '7')
             .replace(/[BRPE]/g, '8')
             .replace(/[gq]/g, '9')
             .replace(/[^0-9.,]/g, '');
           if (val !== '.' && val !== ',' && val !== '') {
             percentage = val;
             tokens.splice(i, 1);
             break;
           }
        }
      }
      
      if (percentage) {
        while(tokens.length > 0 && /^[0-9]{1,2}$/.test(tokens[tokens.length-1])) {
          tokens.pop();
        }
      }

      let name = tokens.join(' ').replace(/\s+/g, ' ').trim();
      name = name.replace(/^[-:.,;]+|[-:.,;]+$/g, '').trim();

      const typoDictionary = [
        { typo: /\balcohel\b/gi, fix: 'alcohol' },
        { typo: /\bcomponenis\b/gi, fix: 'components' },
        { typo: /\bglyczl\b/gi, fix: 'glycol' },
        { typo: /\bdiprogylene\b/gi, fix: 'dipropylene' },
        { typo: /p=p-40/gi, fix: 'peg-40' },
        { typo: /p-p-40/gi, fix: 'peg-40' },
        { typo: /p=g-40/gi, fix: 'peg-40' },
        { typo: /\bci 11820\b/gi, fix: 'ci 11920' },
        { typo: /\bqlycerin\b/gi, fix: 'glycerin' },
        { typo: /\bglycetin\b/gi, fix: 'glycerin' },
        { typo: /\bwaler\b/gi, fix: 'water' },
        { typo: /\bparfim\b/gi, fix: 'parfum' },
        { typo: /\boxiract\b/gi, fix: 'extract' },
        { typo: /\bcxtract\b/gi, fix: 'extract' }
      ];
      
      typoDictionary.forEach(({ typo, fix }) => {
        name = name.replace(typo, fix);
      });

      if (!name && rawLine) name = rawLine;

      parsedIngredients.push({
        name: name,
        function: func,
        cas: cas,
        percentage: percentage
      });
    });

    let sum = 0;
    let maxIdx = -1;
    let maxVal = -1;
    
    parsedIngredients.forEach((ing, idx) => {
       const pStr = String(ing.percentage).replace(',', '.').replace(/[^0-9.]/g, '');
       const p = parseFloat(pStr) || 0;
       if (p > maxVal) {
          maxVal = p;
          maxIdx = idx;
       }
       sum += p;
    });

    if (Math.abs(sum - 100) > 0.01 && maxIdx >= 0) {
       let sumOthers = 0;
       parsedIngredients.forEach((ing, idx) => {
         if (idx !== maxIdx) {
            const pStr = String(ing.percentage).replace(',', '.').replace(/[^0-9.]/g, '');
            sumOthers += (parseFloat(pStr) || 0);
         }
       });
       
       if (sumOthers < 100) {
          const corrected = 100 - sumOthers;
          parsedIngredients[maxIdx].percentage = parseFloat(corrected.toFixed(2));
       }
    }
    return parsedIngredients;
  };

  const handleScanComplete = (text) => {
    try {
      const textLines = text.split('\n').filter(line => line.trim().length > 0);
      const parsedIngredients = parseTextLinesToIngredients(textLines, true);
      
      if (parsedIngredients.length > 0) {
        setIngredients(prev => {
          if (prev.length === 1 && !prev[0].name) return parsedIngredients;
          return [...prev, ...parsedIngredients];
        });
      }
      setIsScanModalOpen(false);
    } catch (err) {
      console.error('Failed to process text from image', err);
      setIsScanModalOpen(false);
    }
  };

  const handlePasteSubmit = () => {
    try {
      if (!pastedText.trim()) return;
      
      let lines = pastedText.split('\n').map(l => l.trim()).filter(l => l !== '');
      const hasVerticalNumbers = lines.some(l => /^\d+[\.,]$/.test(l));
      
      let textLines = [];
      if (hasVerticalNumbers) {
         let currentRow = [];
         let insideTable = false;
         
         for (let i = 0; i < lines.length; i++) {
           const line = lines[i];
           const lower = line.toLowerCase();
           
           if (/^1[\.,]$/.test(line)) {
             insideTable = true;
           }
           if (lower.includes('total percentage') || lower.includes('total fercentage')) {
             insideTable = false;
           }
           
           if (insideTable) {
             if (/^\d+[\.,]$/.test(line)) {
               if (currentRow.length > 0) {
                 textLines.push(currentRow.join(' '));
               }
               currentRow = [line];
             } else {
               currentRow.push(line);
             }
           }
         }
         if (currentRow.length > 0) {
           textLines.push(currentRow.join(' '));
         }
      } else {
         textLines = lines;
      }
      
      const parsedIngredients = parseTextLinesToIngredients(textLines, false);
      
      if (parsedIngredients.length > 0) {
        setIngredients(prev => {
          if (prev.length === 1 && !prev[0].name) return parsedIngredients;
          return [...prev, ...parsedIngredients];
        });
      }
      setIsPasteModalOpen(false);
      setPastedText('');
    } catch (err) {
      console.error('Failed to process pasted text', err);
      setIsPasteModalOpen(false);
      setPastedText('');
    }
  };

  const upscaleImage = (file) => {
    return new Promise((resolve) => {
      const img = new window.Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const scale = 3; // Upscale 3x to make small text readable
        canvas.width = img.width * scale;
        canvas.height = img.height * scale;
        const ctx = canvas.getContext('2d');
        // Draw with better contrast and sharpness
        ctx.imageSmoothingEnabled = false;
        
        // Fill white background just in case
        ctx.fillStyle = 'white';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        
        // Smart Background Removal:
        // Converts light backgrounds (like the blue highlight on row 6) to pure white,
        // but keeps dark text pixels untouched to preserve anti-aliasing!
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const data = imageData.data;
        for (let i = 0; i < data.length; i += 4) {
          const r = data[i];
          const g = data[i + 1];
          const b = data[i + 2];
          
          const v = 0.2126 * r + 0.7152 * g + 0.0722 * b;
          if (v > 160) { // Light pixel (background like blue highlight) -> make it white
             data[i] = 255;
             data[i + 1] = 255;
             data[i + 2] = 255;
          }
          // Dark pixels (text) are left exactly as they are to remain smooth!
        }
        ctx.putImageData(imageData, 0, 0);
        
        resolve(canvas.toDataURL('image/png'));
      };
      img.src = URL.createObjectURL(file);
    });
  };

  const handleProcessFile = async (file) => {
    if (!file) return;

    try {
      setIsScanning(true);
      toast.loading('Sedang memindai gambar... (Membutuhkan beberapa saat)', { id: 'ocr-toast' });
      
      const upscaledImage = await upscaleImage(file);

      const worker = await createWorker('eng');
      await worker.setParameters({
        tessedit_pageseg_mode: '6', // Assume a single uniform block of text (ensures no table rows are skipped, our custom clustering handles the rest)
      });
      const result = await worker.recognize(upscaledImage);
      
      let textLines = [];
      if (result.data && result.data.words && result.data.words.length > 0) {
        // Robust Table Clustering Algorithm (Group words by Y center moving average)
        const words = result.data.words;
        
        words.sort((a, b) => {
           const cyA = (a.bbox.y0 + a.bbox.y1) / 2;
           const cyB = (b.bbox.y0 + b.bbox.y1) / 2;
           return cyA - cyB;
        });

        const rows = [];
        let currentRow = [words[0]];
        let currentRowCenter = (words[0].bbox.y0 + words[0].bbox.y1) / 2;

        for (let i = 1; i < words.length; i++) {
          const word = words[i];
          const wordCenterY = (word.bbox.y0 + word.bbox.y1) / 2;
          
          // 25px threshold for 3x upscaled image (roughly half a line height)
          if (Math.abs(wordCenterY - currentRowCenter) < 25) {
            currentRow.push(word);
            currentRowCenter = currentRow.reduce((s, w) => s + (w.bbox.y0 + w.bbox.y1)/2, 0) / currentRow.length;
          } else {
            rows.push(currentRow);
            currentRow = [word];
            currentRowCenter = wordCenterY;
          }
        }
        if (currentRow.length > 0) {
          rows.push(currentRow);
        }

        // Sort words in each row by X coordinate and join
        textLines = rows.map(row => {
          row.sort((a, b) => a.bbox.x0 - b.bbox.x0);
          return row.map(w => w.text).join(' ');
        });
      } else if (result.data && result.data.lines) {
        textLines = result.data.lines.map(line => line.text.trim()).filter(Boolean);
      } else if (result.data && result.data.text) {
        textLines = result.data.text.split('\n').map(line => line.trim()).filter(Boolean);
      }
      
      await worker.terminate();
      
      const parsedIngredients = parseTextLinesToIngredients(textLines, true);
      
      if (parsedIngredients.length > 0) {
        setIngredients(prev => {
          // If only empty default exists, replace it
          if (prev.length === 1 && !prev[0].name) return parsedIngredients;
          return [...prev, ...parsedIngredients];
        });
        toast.success('Berhasil mengekstrak bahan! Silakan cek dan rapikan datanya.', { id: 'ocr-toast' });
        setIsScanModalOpen(false);
      } else {
        toast.error('Tidak ada data bahan yang terbaca.', { id: 'ocr-toast' });
      }
    } catch (err) {
      console.error(err);
      toast.error(`Gagal melakukan proses scan: ${err.message || err}`, { id: 'ocr-toast' });
    } finally {
      setIsScanning(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const onDragOver = useCallback((e) => {
    e.preventDefault();
    setDragActive(true);
  }, []);

  const onDragLeave = useCallback((e) => {
    e.preventDefault();
    setDragActive(false);
  }, []);

  const onDrop = useCallback((e) => {
    e.preventDefault();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleProcessFile(e.dataTransfer.files[0]);
    }
  }, []);

  // Listen for Paste events when modal is open
  useEffect(() => {
    const handlePaste = (e) => {
      if (!isScanModalOpen) return;
      
      const items = e.clipboardData?.items;
      if (!items) return;

      for (let i = 0; i < items.length; i++) {
        if (items[i].type.indexOf('image') !== -1) {
          const blob = items[i].getAsFile();
          handleProcessFile(blob);
          break;
        }
      }
    };

    window.addEventListener('paste', handlePaste);
    return () => window.removeEventListener('paste', handlePaste);
  }, [isScanModalOpen]);

  const generateDocument = async () => {
    if (!variantName || !brandName) {
      toast.error('Merek dan Nama Varian harus diisi');
      return;
    }

    try {
      setIsGenerating(true);
      
      const response = await fetch('/templates/dip-template.docx?v=' + Date.now());
      if (!response.ok) {
        throw new Error('Template file tidak ditemukan (public/templates/dip-template.docx)');
      }
      
      const content = await response.arrayBuffer();

      const fallbackBase64 = 'iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=';
      function base64ToArrayBuffer(base64) {
          var binary_string = window.atob(base64);
          var len = binary_string.length;
          var bytes = new Uint8Array(len);
          for (var i = 0; i < len; i++) {
              bytes[i] = binary_string.charCodeAt(i);
          }
          return bytes.buffer;
      }
      const fallbackPng = base64ToArrayBuffer(fallbackBase64);

      let signatureBuffer = fallbackPng;
      try {
        const sigUrl = '/signature-yosita.png';
        const res = await fetch(sigUrl);
        if (res.ok) {
          const buf = await res.arrayBuffer();
          if (buf.byteLength > 0) signatureBuffer = buf;
        } else {
          console.error('Failed to fetch signature:', res.status);
        }
      } catch (e) {
        console.warn('Gagal memuat tanda tangan', e);
      }
      
      const zip = new PizZip(content);

      const imageOpts = {
        centered: false,
        getImage: function(tagValue, tagName) {
          if (tagName === 'signature') {
            return signatureBuffer;
          }
          return new ArrayBuffer(0);
        },
        getSize: function(img, tagValue, tagName) {
          return [150, 75]; // Lebar 150px, Tinggi 75px
        }
      };
      
      const imageModule = new ImageModule(imageOpts);

      const doc = new Docxtemplater(zip, {
        paragraphLoop: true,
        linebreaks: true,
        modules: [imageModule]
      });

      const data = {
        merek_brand: (brandName || '').toUpperCase(),
        nama_variant: (variantName || '').toUpperCase(),
        signature: true,
        kemasan: kemasanList.filter(k => k.name.trim() !== '').map(k => {
          let val = k.name.trim();
          if (/^\d+$/.test(val)) {
            val += ' mL';
          } else if (/ml$/i.test(val)) {
            val = val.replace(/\s*ml$/i, ' mL');
          }
          return { name: val };
        }),
        warna_sediaan: warnaSediaan,
        vendors_xml: vendors.filter(v => v.name.trim() !== '').map((v) => {
          const supplierData = suppliers.find(s => s.nama_sales === v.name) || {};
          const name = v.name;
          const alamat = supplierData.alamat || '-';
          const telp = supplierData.phone || '-';
          return `<w:p><w:pPr><w:pStyle w:val="ListParagraph"/><w:rPr><w:rFonts w:ascii="Times New Roman" w:hAnsi="Times New Roman" w:cs="Times New Roman"/></w:rPr></w:pPr><w:r><w:rPr><w:rFonts w:ascii="Times New Roman" w:hAnsi="Times New Roman" w:cs="Times New Roman"/></w:rPr><w:t xml:space="preserve">Nama Pemasok</w:t></w:r><w:r><w:rPr><w:rFonts w:ascii="Times New Roman" w:hAnsi="Times New Roman" w:cs="Times New Roman"/></w:rPr><w:tab/></w:r><w:r><w:rPr><w:rFonts w:ascii="Times New Roman" w:hAnsi="Times New Roman" w:cs="Times New Roman"/></w:rPr><w:tab/></w:r><w:r><w:rPr><w:rFonts w:ascii="Times New Roman" w:hAnsi="Times New Roman" w:cs="Times New Roman"/></w:rPr><w:t xml:space="preserve">: </w:t></w:r><w:r><w:rPr><w:rFonts w:ascii="Times New Roman" w:hAnsi="Times New Roman" w:cs="Times New Roman"/></w:rPr><w:t>${name.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')}</w:t></w:r></w:p><w:p><w:pPr><w:pStyle w:val="ListParagraph"/><w:ind w:left="2880" w:hanging="2160"/><w:rPr><w:rFonts w:ascii="Times New Roman" w:hAnsi="Times New Roman" w:cs="Times New Roman"/></w:rPr></w:pPr><w:r><w:rPr><w:rFonts w:ascii="Times New Roman" w:hAnsi="Times New Roman" w:cs="Times New Roman"/></w:rPr><w:t xml:space="preserve">Alamat Pemasok</w:t></w:r><w:r><w:rPr><w:rFonts w:ascii="Times New Roman" w:hAnsi="Times New Roman" w:cs="Times New Roman"/></w:rPr><w:tab/></w:r><w:r><w:rPr><w:rFonts w:ascii="Times New Roman" w:hAnsi="Times New Roman" w:cs="Times New Roman"/></w:rPr><w:t xml:space="preserve">: </w:t></w:r><w:r><w:rPr><w:rFonts w:ascii="Times New Roman" w:hAnsi="Times New Roman" w:cs="Times New Roman"/></w:rPr><w:t>${alamat.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')}</w:t></w:r></w:p><w:p><w:pPr><w:pStyle w:val="ListParagraph"/><w:rPr><w:rFonts w:ascii="Times New Roman" w:hAnsi="Times New Roman" w:cs="Times New Roman"/></w:rPr></w:pPr><w:r><w:rPr><w:rFonts w:ascii="Times New Roman" w:hAnsi="Times New Roman" w:cs="Times New Roman"/></w:rPr><w:t>Telp</w:t></w:r><w:r><w:rPr><w:rFonts w:ascii="Times New Roman" w:hAnsi="Times New Roman" w:cs="Times New Roman"/></w:rPr><w:tab/></w:r><w:r><w:rPr><w:rFonts w:ascii="Times New Roman" w:hAnsi="Times New Roman" w:cs="Times New Roman"/></w:rPr><w:tab/></w:r><w:r><w:rPr><w:rFonts w:ascii="Times New Roman" w:hAnsi="Times New Roman" w:cs="Times New Roman"/></w:rPr><w:tab/><w:t xml:space="preserve">: </w:t></w:r><w:r><w:rPr><w:rFonts w:ascii="Times New Roman" w:hAnsi="Times New Roman" w:cs="Times New Roman"/></w:rPr><w:t>${telp.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')}</w:t></w:r></w:p><w:p><w:pPr><w:rPr><w:rFonts w:ascii="Times New Roman" w:hAnsi="Times New Roman" w:cs="Times New Roman"/></w:rPr></w:pPr></w:p>`;
        }).join(''),
        bahan: ingredients.filter(i => i.name.trim() !== '').map((i, idx) => {
          const bahan_text_val = ingredients.filter(x => x.name.trim() !== '' && !x.name.toLowerCase().includes('fragrance') && !x.name.toLowerCase().includes('parfum')).map(x => x.name).join(', ');
          return {
            ...i,
            name: i.name.replace(/\b\w/g, l => l.toUpperCase()),
            no: idx + 1,
            cas_no: i.cas || '-',
            cara_pembuatan: `@@CARA_PEMBUATAN_${idx===0?'TRUE':'FALSE'}@@Timbang bahan dan siapkan alat yang diperlukan, masukan bibit parfum kedalam mixer, tambahkan ${bahan_text_val} dan campur hingga homogen terlarut selama kurang lebih 1 jam, setelah homogen lalu dituangkan kedalam mesin filling parfum, kemas dalam kemasan botol sesuai ukuran.`
          };
        })
      };

      doc.render(data);

      const generatedZip = doc.getZip();
      let xml = generatedZip.file("word/document.xml").asText();
      
      // Fix vMerge natively processing cell by cell
      const hasMultipleIngredients = xml.includes('@@CARA_PEMBUATAN_FALSE@@');
      
      xml = xml.replace(/<w:tc>([\s\S]*?)<\/w:tc>/g, (match) => {
        if (match.includes('@@CARA_PEMBUATAN_FALSE@@')) {
          match = match.replace(/<w:vMerge[^>]*\/>/g, '<w:vMerge w:val="continue"/>');
          match = match.replace('@@CARA_PEMBUATAN_FALSE@@', '');
        } else if (match.includes('@@CARA_PEMBUATAN_TRUE@@')) {
          if (!hasMultipleIngredients) {
            match = match.replace(/<w:vMerge[^>]*\/>/g, '');
          } else {
            match = match.replace(/<w:vMerge[^>]*\/>/g, '<w:vMerge w:val="restart"/>');
          }
          match = match.replace('@@CARA_PEMBUATAN_TRUE@@', '');
        }
        return match;
      });

      // (w14 tracking IDs are intentionally preserved to prevent corruption in Word 365)

      generatedZip.file("word/document.xml", xml);

      const out = generatedZip.generate({
        type: 'blob',
        mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        compression: 'DEFLATE',
      });

      const ukuran = data.kemasan.length > 0 ? data.kemasan[0].name.replace(/\s+/g, '').toUpperCase() : '';
      const bName = (brandName || '').toUpperCase();
      const vName = (variantName || '').toUpperCase();
      const fileNameStr = `DIP ${bName} ${vName} ${ukuran}`.replace(/\s+/g, ' ').trim();

      saveAs(out, `${fileNameStr}.docx`);
      toast.success('DIP berhasil di-generate!');
    } catch (error) {
      console.error('Error generating document:', error);
      toast.error(error.message || 'Gagal men-generate dokumen');
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="p-4 lg:p-8 max-w-6xl mx-auto space-y-6 pb-24">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 font-cabinet">DIP Generator</h1>
        <p className="text-gray-500 mt-1">Buat Dokumen Informasi Produk secara otomatis menggunakan template.</p>
      </div>

      <div className="bg-blue-50/50 border border-blue-200 rounded-xl p-4 flex gap-4 text-blue-800">
        <AlertCircle className="w-6 h-6 flex-shrink-0 text-blue-500" />
        <div className="text-sm">
          <p className="font-semibold mb-1">Panduan Template Word</p>
          <p>Pastikan file template <code>DIP PRINCE OF DOHA 50ML.docx</code> sudah memiliki variabel berikut agar terganti otomatis:</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
            <ul className="list-disc pl-5 space-y-1">
              <li>Merek: <code>{`{merek_brand}`}</code></li>
              <li>Varian: <code>{`{nama_variant}`}</code></li>
              <li>Vendor: <code>{`{#vendors} {name} {/vendors}`}</code></li>
              <li>Kemasan: <code>{`{#kemasan} {name} {/kemasan}`}</code></li>
            </ul>
            <ul className="list-disc pl-5 space-y-1">
              <li><strong>Tabel Bahan:</strong> Gunakan tag loop <code>{`{#bahan}`}</code></li>
              <li>Nama: <code>{`{name}`}</code></li>
              <li>Fungsi: <code>{`{function}`}</code></li>
              <li>CAS: <code>{`{cas}`}</code></li>
              <li>Kadar: <code>{`{percentage}`}</code></li>
              <li>Akhiri dengan: <code>{`{/bahan}`}</code></li>
            </ul>
          </div>
        </div>
      </div>

      <Card className="border border-gray-200/60 shadow-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-gray-500" />
            Data Informasi Produk
          </CardTitle>
          <CardDescription>Masukkan data yang akan disisipkan ke dalam dokumen.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-8">
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Merek / Brand</Label>
              <Input 
                placeholder="Contoh: DERMATECH" 
                value={brandName}
                onChange={(e) => setBrandName(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Nama Varian</Label>
              <Input 
                placeholder="Contoh: PRINCE OF DOHA 50ML" 
                value={variantName}
                onChange={(e) => setVariantName(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Warna Sediaan</Label>
              <SearchableCombobox
                options={warnaOptionsDb.map(opt => ({ value: opt, label: opt }))}
                value={warnaSediaan}
                onValueChange={handleWarnaChange}
                placeholder="Pilih atau ketik warna sediaan..."
                allowCustom={true}
              />
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label>Daftar Pemasok / Vendor</Label>
              <Button type="button" variant="outline" size="sm" onClick={addVendor} className="h-8">
                <Plus className="w-4 h-4 mr-1" /> Tambah Vendor
              </Button>
            </div>
            <div className="space-y-2">
              {vendors.map((vendor, index) => (
                <div key={index} className="flex items-center gap-2">
                  <div className="flex-1">
                    <SearchableCombobox
                      options={supplierOptions}
                      value={vendor.name}
                      onValueChange={(val) => updateVendor(index, val)}
                      placeholder="Pilih atau ketik vendor..."
                      allowCustom={true}
                    />
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="text-red-500 hover:text-red-700 hover:bg-red-50"
                    onClick={() => removeVendor(index)}
                    disabled={vendors.length === 1}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label>Data Ukuran Kemasan</Label>
              <Button type="button" variant="outline" size="sm" onClick={addKemasan} className="h-8">
                <Plus className="w-4 h-4 mr-1" /> Tambah Ukuran
              </Button>
            </div>
            <div className="space-y-2">
              {kemasanList.map((kemasan, index) => (
                <div key={index} className="flex items-center gap-2">
                  <Input
                    placeholder="Contoh: Botol Kaca 50 mL"
                    value={kemasan.name}
                    onChange={(e) => updateKemasan(index, e.target.value)}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="text-red-500 hover:text-red-700 hover:bg-red-50"
                    onClick={() => removeKemasan(index)}
                    disabled={kemasanList.length === 1}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-3 border-t border-gray-100 pt-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <Label className="text-base font-bold text-gray-800">Tabel Komposisi Produk (Racikan)</Label>
                <p className="text-sm text-gray-500">Isi manual atau scan screenshot BPOM untuk mengisi tabel.</p>
              </div>
              <div className="flex items-center gap-2">
                <Button 
                  type="button" 
                  variant="secondary" 
                  className="h-9 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border border-emerald-200"
                  onClick={() => setIsPasteModalOpen(true)}
                >
                  <ClipboardList className="w-4 h-4 mr-2" /> 
                  Paste Teks BPOM
                </Button>
                <Button 
                  type="button" 
                  variant="secondary" 
                  className="h-9 bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200"
                  onClick={() => setIsScanModalOpen(true)}
                  disabled={isScanning}
                >
                  <Scan className="w-4 h-4 mr-2" /> 
                  Scan OCR BPOM
                </Button>
                <Button type="button" variant="outline" onClick={addIngredient} className="h-9">
                  <Plus className="w-4 h-4 mr-1" /> Tambah Baris
                </Button>
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setIngredients([{ name: '', function: '', cas: '', percentage: '' }])} 
                  className="h-9 text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                  disabled={ingredients.length === 1 && !ingredients[0].name}
                >
                  <Trash2 className="w-4 h-4 mr-1" /> Hapus Semua
                </Button>
              </div>
            </div>
            
            <div className="max-h-[600px] overflow-y-auto rounded-lg border border-gray-200 bg-slate-50/50">
              <table className="w-full text-sm text-left">
                <thead className="text-xs text-slate-500 uppercase bg-slate-100/50 sticky top-0 z-10 shadow-sm">
                  <tr>
                    <th className="px-4 py-3 font-semibold w-12 text-center">No</th>
                    <th className="px-4 py-3 font-semibold">Nama Bahan</th>
                    <th className="px-4 py-3 font-semibold w-32 text-center">Kadar (%)</th>
                    <th className="px-4 py-3 font-semibold w-40">CAS No.</th>
                    <th className="px-4 py-3 font-semibold w-48">Fungsi</th>
                    <th className="px-4 py-3 w-16"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {ingredients.map((ingredient, index) => (
                    <tr key={index} className="bg-white hover:bg-slate-50/50 transition-colors">
                      <td className="px-4 py-2 text-center text-slate-400 font-medium">{index + 1}</td>
                      <td className="px-4 py-2">
                        <Input
                          placeholder="Nama bahan..."
                          value={ingredient.name}
                          onChange={(e) => updateIngredient(index, 'name', e.target.value)}
                          className="h-9 border-slate-200"
                        />
                      </td>
                      <td className="px-4 py-2">
                        <Input
                          placeholder="0.00"
                          value={ingredient.percentage}
                          onChange={(e) => updateIngredient(index, 'percentage', e.target.value)}
                          className="h-9 border-slate-200 text-center font-mono"
                        />
                      </td>
                      <td className="px-4 py-2">
                        <Input
                          placeholder="CAS No..."
                          value={ingredient.cas}
                          onChange={(e) => updateIngredient(index, 'cas', e.target.value)}
                          className="h-9 border-slate-200 font-mono text-xs"
                        />
                      </td>
                      <td className="px-4 py-2">
                        <Input
                          placeholder="Fungsi..."
                          value={ingredient.function}
                          onChange={(e) => updateIngredient(index, 'function', e.target.value)}
                          className="h-9 border-slate-200"
                        />
                      </td>
                      <td className="px-4 py-2 text-right">
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="text-red-400 hover:text-red-700 hover:bg-red-50 h-8 w-8"
                          onClick={() => removeIngredient(index)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                  {ingredients.length === 0 && (
                    <tr>
                      <td colSpan={6} className="px-4 py-12 text-center text-slate-400">
                        <ImageIcon className="w-8 h-8 mx-auto mb-2 opacity-20" />
                        Belum ada bahan. Silakan scan gambar BPOM atau tambah baris.
                      </td>
                    </tr>
                  )}
                </tbody>
                {ingredients.length > 0 && (
                  <tfoot className="bg-slate-50 font-medium">
                    <tr>
                      <td colSpan={2} className="px-4 py-3 text-right text-slate-600">Total Persentase:</td>
                      <td className="px-4 py-3 text-center">
                        <div className={cn(
                          "inline-flex items-center justify-center px-2.5 py-1 rounded-md text-xs font-bold",
                          (() => {
                            const total = ingredients.reduce((sum, ing) => {
                              const pStr = String(ing.percentage).replace(',', '.').replace(/[^0-9.]/g, '');
                              return sum + (parseFloat(pStr) || 0);
                            }, 0);
                            if (Math.abs(total - 100) < 0.01) return "bg-emerald-100 text-emerald-700";
                            return "bg-red-100 text-red-700";
                          })()
                        )}>
                          {(() => {
                            const total = ingredients.reduce((sum, ing) => {
                              const pStr = String(ing.percentage).replace(',', '.').replace(/[^0-9.]/g, '');
                              return sum + (parseFloat(pStr) || 0);
                            }, 0);
                            return `${total.toFixed(2).replace(/\.00$/, '')}%`;
                          })()}
                        </div>
                      </td>
                      <td colSpan={3} className="px-4 py-3 text-xs text-slate-500">
                        {(() => {
                          const total = ingredients.reduce((sum, ing) => {
                            const pStr = String(ing.percentage).replace(',', '.').replace(/[^0-9.]/g, '');
                            return sum + (parseFloat(pStr) || 0);
                          }, 0);
                          if (Math.abs(total - 100) < 0.01) return <span className="text-emerald-600 font-semibold">✓ Sempurna (100%)</span>;
                          if (total < 100) return <span className="text-red-600">Kurang {(100 - total).toFixed(2).replace(/\.00$/, '')}% lagi</span>;
                          return <span className="text-red-600">Kelebihan {(total - 100).toFixed(2).replace(/\.00$/, '')}%</span>;
                        })()}
                      </td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>

          <div className="pt-8 border-t border-gray-100">
            <Button 
              className="w-full h-14 text-base font-semibold bg-gray-900 hover:bg-gray-800 text-white shadow-md transition-all hover:shadow-lg active:scale-[0.99]"
              onClick={generateDocument}
              disabled={isGenerating || isScanning}
            >
              {isGenerating ? (
                <span className="flex items-center gap-2">
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Menyusun Dokumen DIP...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Download className="w-5 h-5" />
                  Generate Dokumen DIP
                </span>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* OCR Scan Modal */}
      <Dialog open={isScanModalOpen} onOpenChange={setIsScanModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Scan Gambar BPOM (OCR)</DialogTitle>
            <DialogDescription>
              Drag & Drop gambar screenshot tabel BPOM ke kotak di bawah, atau tekan <strong>Ctrl + V</strong> untuk langsung mem-paste dari clipboard.
            </DialogDescription>
          </DialogHeader>
          
          <div 
            className={cn(
              "mt-4 border-2 border-dashed rounded-xl p-10 flex flex-col items-center justify-center text-center transition-colors cursor-pointer",
              dragActive ? "border-blue-500 bg-blue-50" : "border-slate-300 bg-slate-50 hover:bg-slate-100"
            )}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            onDrop={onDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <input 
              type="file" 
              accept="image/*" 
              className="hidden" 
              ref={fileInputRef}
              onChange={(e) => handleProcessFile(e.target.files[0])}
            />
            <div className="w-16 h-16 bg-white shadow-sm rounded-full flex items-center justify-center mb-4 text-blue-500">
              {isScanning ? (
                <div className="w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
              ) : (
                <UploadCloud className="w-8 h-8" />
              )}
            </div>
            
            {isScanning ? (
              <div>
                <p className="font-semibold text-slate-700">Membaca teks dari gambar...</p>
                <p className="text-xs text-slate-500 mt-1">Harap tunggu, proses ini memakan waktu beberapa detik.</p>
              </div>
            ) : (
              <div>
                <p className="font-semibold text-slate-700">Drag & Drop Gambar ke Sini</p>
                <p className="text-sm text-slate-500 mt-1">atau klik untuk memilih file dari komputer</p>
                <div className="mt-4 inline-flex items-center gap-1.5 px-3 py-1.5 bg-slate-200/50 rounded-md text-xs font-medium text-slate-600">
                  <span className="bg-white border shadow-sm rounded px-1.5 py-0.5">Ctrl</span>
                  <span>+</span>
                  <span className="bg-white border shadow-sm rounded px-1.5 py-0.5">V</span>
                  <span className="ml-1">untuk Paste</span>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Paste Text Modal */}
      <Dialog open={isPasteModalOpen} onOpenChange={setIsPasteModalOpen}>
        <DialogContent className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle>Paste Teks Tabel BPOM</DialogTitle>
            <DialogDescription>
              Copy teks tabel langsung dari halaman web BPOM lalu paste ke kotak di bawah ini. Pastikan Anda mencopy mulai dari nama bahan hingga persentase.
            </DialogDescription>
          </DialogHeader>
          
          <div className="mt-4">
            <textarea
              className="w-full h-64 p-3 border rounded-md font-mono text-sm resize-none focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500"
              placeholder="Paste di sini... (Ctrl+V)&#10;&#10;Contoh hasil copy:&#10;1. ethanol solvent 64-17-5 66&#10;Atau format vertikal juga didukung."
              value={pastedText}
              onChange={(e) => setPastedText(e.target.value)}
              autoFocus
            />
          </div>
          <div className="mt-4 flex justify-end">
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => setIsPasteModalOpen(false)}
              className="mr-2"
            >
              Batal
            </Button>
            <Button 
              type="button" 
              onClick={handlePasteSubmit}
              disabled={!pastedText.trim()}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              Proses Teks
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
